import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, Lock, Mail, Phone, ShieldCheck } from "lucide-react";
import { EMAIL, EMAIL_HREF, HERO_IMG, PHONE, PHONE_HREF } from "../../lib/site";

const RATE_BULLETS = [
  "No origination fees on personal loans",
  "Soft-pull pre-qualification",
  "Human advisor on every file",
  "Same-day decisions",
];

const STATS = [
  { k: "$2.4B+", v: "Capital Deployed" },
  { k: "48,000+", v: "Clients Served" },
  { k: "4.9/5", v: "Client Rating" },
  { k: "24 hrs", v: "Median Decision" },
];

export default function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="Luxury architecture at night" className="h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#050A1F] via-[#050A1F]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050A1F] via-transparent to-transparent" />
      </div>

      <div className="relative mx-auto grid max-w-7xl gap-14 px-6 pt-24 pb-32 lg:grid-cols-12 lg:px-10 lg:pt-32 lg:pb-40">
        <div className="lg:col-span-8">
          <div className="llu-reveal flex items-center gap-3 text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">
            <span className="h-px w-8 bg-[#D4AF37]" />
            Private Lending · Est. 2014
          </div>
          <h1 className="llu-reveal-2 mt-8 font-serif text-5xl font-light leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl">
            Capital that respects
            <br />
            <span className="italic text-[#D4AF37]">your standard.</span>
          </h1>
          <p className="llu-reveal-3 mt-8 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">
            Personal and business financing engineered for people who value discretion, speed and precision.
            Underwritten by humans. Delivered through infrastructure trusted by American banks.
          </p>

          <div className="llu-reveal-4 mt-10 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            <Link
              to="/apply"
              data-testid="hero-apply-btn"
              className="group inline-flex items-center gap-3 border border-[#D4AF37] bg-[#D4AF37] px-8 py-4 text-xs uppercase tracking-[0.32em] text-[#050A1F] transition-colors hover:bg-[#F3E5AB]"
            >
              Grab Your Loan Now
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href="#services"
              data-testid="hero-explore-btn"
              className="inline-flex items-center gap-2 border border-white/20 px-8 py-4 text-xs uppercase tracking-[0.32em] text-white/90 transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]"
            >
              Explore Services
            </a>
          </div>

          <div className="llu-reveal-4 mt-12 flex flex-wrap items-center gap-6 text-xs text-white/60">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-[#D4AF37]" strokeWidth={1.6} />
              <span className="uppercase tracking-[0.24em]">Bank-Grade Security</span>
            </div>
            <span className="text-white/20">·</span>
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-[#D4AF37]" strokeWidth={1.6} />
              <span className="uppercase tracking-[0.24em]">256-bit Encryption</span>
            </div>
            <span className="text-white/20">·</span>
            <a href={PHONE_HREF} className="flex items-center gap-2 hover:text-[#D4AF37]" data-testid="hero-phone">
              <Phone className="h-4 w-4 text-[#D4AF37]" strokeWidth={1.6} />
              <span className="tracking-wider">{PHONE}</span>
            </a>
            <span className="text-white/20">·</span>
            <a href={EMAIL_HREF} className="flex items-center gap-2 hover:text-[#D4AF37]" data-testid="hero-email">
              <Mail className="h-4 w-4 text-[#D4AF37]" strokeWidth={1.6} />
              <span className="tracking-wider">{EMAIL}</span>
            </a>
          </div>
        </div>

        <aside className="lg:col-span-4">
          <div className="llu-reveal-3 border border-white/10 bg-[#0A1128]/70 p-8 backdrop-blur-xl">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">Rate Preview</div>
            <p className="mt-3 font-serif text-4xl font-light">
              From <span className="text-[#D4AF37]">5.99%</span> APR
            </p>
            <p className="mt-2 text-xs text-white/50">Fixed. Transparent. No prepayment penalty.</p>

            <div className="mt-8 space-y-4">
              {RATE_BULLETS.map((f) => (
                <div key={f} className="flex items-start gap-3 text-sm text-white/80">
                  <ChevronRight className="mt-0.5 h-4 w-4 text-[#D4AF37]" strokeWidth={1.6} />
                  <span>{f}</span>
                </div>
              ))}
            </div>

            <Link
              to="/apply"
              className="mt-8 flex items-center justify-between border-t border-white/10 pt-6 text-xs uppercase tracking-[0.28em] text-[#D4AF37] transition-colors hover:text-[#F3E5AB]"
              data-testid="hero-side-cta"
            >
              Begin Application <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </aside>
      </div>

      <div className="relative border-y border-white/10 bg-[#050A1F]/60 backdrop-blur">
        <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-white/10 px-6 sm:grid-cols-4 lg:px-10">
          {STATS.map((s) => (
            <div key={s.v} className="px-2 py-8 sm:px-6">
              <div className="font-serif text-3xl font-light text-white sm:text-4xl">{s.k}</div>
              <div className="mt-1 text-[10px] uppercase tracking-[0.28em] text-white/50">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
